import React from 'react';
import { useTranslation } from 'react-i18next';

// CEFR ↔ Cambridge ↔ IELTS ↔ Mynawoo, drawn in HTML so it stays crisp and costs no image bytes.
const LEVELS = ['C2', 'C1', 'B2', 'B1', 'A2', 'A1', 'Pre A1'];
const ROW = 100 / LEVELS.length;

type Bar = { from: number; to: number; color: string; text: string; labels: (string | null)[]; className?: string };

const BarCol = ({ from, to, color, text, labels, className = '' }: Bar) => (
    <div
        className={`absolute flex flex-col overflow-hidden rounded-full ${className}`}
        style={{ top: `calc(${from * ROW}% + 4px)`, height: `calc(${(to - from + 1) * ROW}% - 8px)`, background: color }}
    >
        {labels.map((label, i) => (
            <span key={i} className="flex flex-1 items-center px-1.5 text-[7px] font-bold leading-tight lg:px-2 lg:text-[9px]" style={{ color: text }}>
                {label}
            </span>
        ))}
    </div>
);

const Rows = () => (
    <div className="pointer-events-none absolute inset-0 flex flex-col" aria-hidden>
        {LEVELS.map(level => <div key={level} className="flex-1 border-b border-white/[0.05] last:border-0" />)}
    </div>
);

const CefrChart = () => {
    const { t } = useTranslation();
    return (
    <figure className="mx-auto w-full max-w-md select-none" aria-label="CEFR levels compared with Cambridge exams, IELTS bands and Mynawoo courses">
        <div className="grid grid-cols-[3.2rem_1fr_4.2rem] gap-1.5 lg:grid-cols-[4rem_1fr_5rem] lg:gap-2">
            {/* CEFR labels */}
            <div className="flex flex-col rounded-3xl bg-[#1c1c1f] pb-2">
                <p className="h-20 pt-3 text-center text-xs font-bold text-white lg:text-sm">CEFR</p>
                <div className="relative flex h-80 flex-col lg:h-[26rem]">
                    {LEVELS.map(level => (
                        <span key={level} className="flex flex-1 items-center justify-end pe-2 text-center text-xs font-bold text-white lg:text-sm">
                            {level}
                        </span>
                    ))}
                    {([[t('landing.chart_proficient', { defaultValue: 'Proficient' }), 0, 2], [t('landing.chart_independent', { defaultValue: 'Independent' }), 2, 4], [t('landing.chart_basic', { defaultValue: 'Basic' }), 4, 7]] as const).map(([name, a, b]) => (
                        <span
                            key={name}
                            className="absolute start-1 flex rotate-180 items-center justify-center text-[7px] font-bold uppercase tracking-wider text-white/50 [writing-mode:vertical-rl] lg:text-[9px]"
                            style={{ top: `${a * ROW}%`, height: `${(b - a) * ROW}%` }}
                        >
                            {name}
                        </span>
                    ))}
                </div>
            </div>

            {/* Cambridge + IELTS */}
            <div className="flex flex-col rounded-3xl bg-[#1c1c1f] px-1.5 pb-2 lg:px-2">
                <div className="h-20 pt-3">
                    <p className="text-xs font-bold text-white lg:text-sm">Cambridge</p>
                    <div className="mt-1 grid grid-cols-[1fr_1.3fr_1fr] gap-1 text-[7px] font-bold leading-tight lg:text-[9px]">
                        <span className="self-end text-[#FF806E]">{t('landing.chart_schools', { defaultValue: 'Schools' })}</span>
                        <span className="text-[#FFB400]">{t('landing.chart_general', { defaultValue: 'General & Higher Ed.' })} <span className="block text-[#8FA8FF]">{t('landing.chart_business', { defaultValue: 'Business' })}</span></span>
                        <span className="self-end text-center text-[#FF6B7A]">IELTS</span>
                    </div>
                </div>
                <div className="relative h-80 lg:h-[26rem]">
                    <Rows />
                    <BarCol from={0} to={6} color="#F04E37" text="#111318" className="start-0 w-[30%]"
                        labels={['C2 Proficiency', 'C1 Advanced', 'B2 First for Schools', 'B1 Preliminary for Schools', 'A2 Key for Schools · A2 Flyers', 'A1 Movers', 'Pre A1 Starters']} />
                    <BarCol from={0} to={4} color="#FFB400" text="#5a3d00" className="start-[34%] w-[20%]"
                        labels={['C2', 'C1', 'B2', 'B1', 'A2 Key']} />
                    <BarCol from={1} to={3} color="#8FA8FF" text="#1c2a66" className="start-[46%] w-[20%] shadow-[-3px_0_0_#1c1c1f]"
                        labels={['C1 Higher', 'B2 Vantage', 'B1 Prelim.']} />
                    <BarCol from={0} to={3} color="#E3002D" text="#fff" className="end-0 w-[28%] text-center"
                        labels={['8.5 – 9.0', '7.0 – 8.0', '5.5 – 6.5', '4.0 – 5.0']} />
                </div>
            </div>

            {/* Mynawoo */}
            <div className="flex flex-col rounded-3xl bg-[#3b2c86] pb-2">
                <p className="h-20 pt-3 text-center text-xs font-extrabold text-[#C9B8FF] lg:text-sm">Mynawoo</p>
                <div className="relative h-80 lg:h-[26rem]">
                    <div
                        className="absolute inset-x-1.5 flex flex-col rounded-full bg-primary shadow-[0_4px_0_#4c3a9e]"
                        style={{ top: `calc(${0 * ROW}% + 4px)`, height: `calc(${6 * ROW}% - 8px)` }}
                    >
                        {['C2', 'C1', 'B2', 'B1', 'A2', 'A1'].map(level => (
                            <span key={level} className="flex flex-1 items-center justify-center text-xs font-black text-white lg:text-sm">{level}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </figure>
    );
};

export default CefrChart;
